import { useEffect, useState } from "react";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { useNotification } from "../../components/notification/useNotification";
import IWalletDto from "../../interfaces/wallet/IWalletDto";
import { tryCreateNewWallet, tryGetWalletById, tryUpdateWallet } from "../../services/wallet/wallet-service";
import extractErrorMessage from "../../common/utils/errorMessages";
import { useFormik } from "formik";
import {
    Box, Button, Divider, FormHelperText, Grid, IconButton,
    InputLabel, MenuItem, OutlinedInput, Select, Stack, Switch, Typography
} from "@mui/material";
import { CreateEditValidationSchema as CreateEditWalletValidationSchema } from "./create-edit.validation";
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";
import { v4 as uuidv4 } from 'uuid';
import IWalletAddressDto from "../../interfaces/wallet-address/IWalletAddressDto";
import { NetworkEnum } from "../../common/enum/network-enum";
import INetworkDto from "../../interfaces/network/INetworkDto";
import { tryGetNetworkList } from "../../services/network/network-service";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface Props {
    editMode: boolean;
    walletId?: string;
}

export default function CreateEditWalletForm(props: Props) {
    const { editMode, walletId } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();
    const [networkList, setNetworkList] = useState<INetworkDto[]>([]);

    const initialValues: IWalletDto = {
        id: '',
        isActive: true,
        isBalancedWallet: false,
        walletName: '',
        addresses: []
    }

    const formik = useFormik({
        initialValues,
        validationSchema: CreateEditWalletValidationSchema(),
        onSubmit: async (values, { setSubmitting }) => {
            const wallet: IWalletDto = {
                id: editMode ? values.id : uuidv4(),
                walletName: values.walletName,
                isBalancedWallet: values.isBalancedWallet,
                isActive: values.isActive,
                addresses: values.addresses
            }

            try {
                setLoading(true);

                if (!editMode) {
                    const response = await tryCreateNewWallet(wallet);

                    if (response?.status === 201) {
                        displayNotification({ type: 'success', message: 'Carteira criada com sucesso' });
                    }
                } else {
                    const response = await tryUpdateWallet(wallet);

                    if (response?.status === 200) {
                        displayNotification({ type: 'success', message: 'Carteira editada com sucesso' });
                    }
                }
                navigate(getBaseURLWithAuth() + 'wallet');
            }
            catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) })
                setSubmitting(false);
            }
            finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        async function loadInitialData() {
            setLoading(true);
            try {
                const [networksResponse, walletResponse] = await Promise.all([
                    tryGetNetworkList(),
                    editMode && walletId ? tryGetWalletById(walletId) : Promise.resolve(null)
                ]);

                if (networksResponse?.data) {
                    setNetworkList(networksResponse.data);
                }

                if (walletResponse?.data) {
                    updateFormValues(walletResponse.data);
                }
            }
            catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
            }
            finally {
                setLoading(false);
            }
        }

        loadInitialData();
    }, []);

    function updateFormValues(wallet: IWalletDto) {
        formik.setFieldValue("id", wallet.id);
        formik.setFieldValue("walletName", wallet.walletName);
        formik.setFieldValue("isActive", wallet.isActive);
        formik.setFieldValue("isBalancedWallet", wallet.isBalancedWallet);
        formik.setFieldValue("addresses", wallet.addresses ?? []);
    }

    function addAddress() {
        const newAddress: IWalletAddressDto = {
            id: '00000000-0000-0000-0000-000000000000',
            walletId: formik.values.id,
            network: NetworkEnum.Ethereum,
            address: '',
            label: ''
        };
        formik.setFieldValue("addresses", [...(formik.values.addresses ?? []), newAddress]);
    }

    function removeAddress(index: number) {
        const updated = (formik.values.addresses ?? []).filter((_, i) => i !== index);
        formik.setFieldValue("addresses", updated);
    }

    function updateAddress(index: number, field: keyof IWalletAddressDto, value: any) {
        const updated = [...(formik.values.addresses ?? [])];
        updated[index] = { ...updated[index], [field]: value };
        formik.setFieldValue("addresses", updated);
    }

    return (
        <>
            {showLoading === true ? <Loader /> : false}
            <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid sx={{ flex: 3 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Nome da Carteira
                            </InputLabel>
                            <OutlinedInput
                                id="wallet-name"
                                value={formik.values.walletName}
                                name="walletName"
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                placeholder="Escreva aqui o nome da sua carteira"
                                fullWidth
                                error={Boolean(formik.touched.walletName && formik.errors.walletName)}
                            />
                            {formik.touched.walletName && formik.errors.walletName && (
                                <FormHelperText error id="helper-text-walletname-error">
                                    {formik.errors.walletName}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Carteira Balanceada
                            </InputLabel>
                            <Switch
                                id="is-balaced-wallet"
                                checked={formik.values.isBalancedWallet}
                                name="isBalancedWallet"
                                onBlur={formik.handleBlur}
                                onChange={() => formik.setFieldValue("isBalancedWallet", !formik.values.isBalancedWallet)}
                            />
                            {formik.touched.isBalancedWallet && formik.errors.isBalancedWallet && (
                                <FormHelperText error id="helper-text-isBalancedWallet-error">
                                    {formik.errors.isBalancedWallet}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Activo
                            </InputLabel>
                            <Switch
                                id="is-active"
                                checked={formik.values.isActive}
                                name="isActive"
                                onBlur={formik.handleBlur}
                                onChange={() => formik.setFieldValue("isActive", !formik.values.isActive)}
                            />
                            {formik.touched.isActive && formik.errors.isActive && (
                                <FormHelperText error id="helper-text-isactive-error">
                                    {formik.errors.isActive}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Box mx={3} mb={2}>
                    <Divider sx={{ mb: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Endereços Blockchain
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={addAddress}
                            variant="outlined"
                            color="primary"
                        >
                            Adicionar
                        </Button>
                    </Box>

                    <Stack spacing={2}>
                        {(formik.values.addresses ?? []).map((addr, index) => (
                            <Grid container spacing={1} key={index} alignItems="center">
                                <Grid sx={{ flex: 1.5 }}>
                                    <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', letterSpacing: 0.5, mb: 0.5 }}>
                                        Rede
                                    </InputLabel>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={addr.network}
                                        onChange={(e) => updateAddress(index, 'network', Number(e.target.value))}
                                        renderValue={(selected) => {
                                            const net = networkList.find(n => n.value === selected);
                                            return (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {net?.imageUrl
                                                        ? <img src={net.imageUrl} alt={net.label} width={20} height={20} style={{ borderRadius: '50%' }} />
                                                        : <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'action.selected' }} />
                                                    }
                                                    {net?.label ?? selected}
                                                </Box>
                                            );
                                        }}
                                    >
                                        {networkList.map((net) => (
                                            <MenuItem key={net.value} value={net.value}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {net.imageUrl
                                                        ? <img src={net.imageUrl} alt={net.label} width={22} height={22} style={{ borderRadius: '50%' }} />
                                                        : <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'action.selected' }} />
                                                    }
                                                    {net.label}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                                <Grid sx={{ flex: 4 }}>
                                    <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', letterSpacing: 0.5, mb: 0.5 }}>
                                        Endereço
                                    </InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        size="small"
                                        value={addr.address}
                                        placeholder="0x... ou pubkey Solana"
                                        onChange={(e) => {
                                            updateAddress(index, 'address', e.target.value);
                                            formik.setFieldTouched(`addresses[${index}].address`, true, false);
                                        }}
                                        error={Boolean(
                                            (formik.touched.addresses as any)?.[index]?.address &&
                                            (formik.errors.addresses as any)?.[index]?.address
                                        )}
                                    />
                                    {(formik.touched.addresses as any)?.[index]?.address &&
                                        (formik.errors.addresses as any)?.[index]?.address && (
                                            <FormHelperText error>
                                                {(formik.errors.addresses as any)[index].address}
                                            </FormHelperText>
                                        )}
                                </Grid>
                                <Grid sx={{ flex: 2 }}>
                                    <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', letterSpacing: 0.5, mb: 0.5 }}>
                                        Label (opcional)
                                    </InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        size="small"
                                        value={addr.label ?? ''}
                                        placeholder="ex: Metamask principal"
                                        onChange={(e) => updateAddress(index, 'label', e.target.value)}
                                    />
                                </Grid>
                                <Grid sx={{ flex: 0, minWidth: 48 }}>
                                    <Box mt={2.5} mb={1} display="flex" justifyContent="center">
                                        <IconButton
                                            color="error"
                                            onClick={() => removeAddress(index)}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}
                    </Stack>
                </Box>

                <Grid mx={3} mb={3}>
                    <Button
                        disableElevation
                        disabled={formik.isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        Confirmar
                    </Button>
                </Grid>
            </form>
        </>
    )
}
